export const APP_TIMEZONE = "America/Sao_Paulo"

const formatterCache = new Map<string, Intl.DateTimeFormat>()

function getFormatter(timeZone: string): Intl.DateTimeFormat {
  let f = formatterCache.get(timeZone)
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    })
    formatterCache.set(timeZone, f)
  }
  return f
}

export interface ZonedParts {
  year: number
  month: number
  day: number
  hour: number
  minute: number
  second: number
}

export function utcInstantToZonedParts(instant: Date, timeZone: string = APP_TIMEZONE): ZonedParts {
  const out: ZonedParts = { year: 0, month: 1, day: 1, hour: 0, minute: 0, second: 0 }
  for (const p of getFormatter(timeZone).formatToParts(instant)) {
    switch (p.type) {
      case "year":
        out.year = Number(p.value)
        break
      case "month":
        out.month = Number(p.value)
        break
      case "day":
        out.day = Number(p.value)
        break
      case "hour":
        out.hour = Number(p.value)
        break
      case "minute":
        out.minute = Number(p.value)
        break
      case "second":
        out.second = Number(p.value)
        break
    }
  }
  if (out.hour === 24) out.hour = 0
  return out
}

function zoneOffsetMs(instant: Date, timeZone: string): number {
  const p = utcInstantToZonedParts(instant, timeZone)
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second)
  return asUtc - Math.floor(instant.getTime() / 1000) * 1000
}

function parseYmd(value: unknown): [number, number, number] | null {
  if (typeof value !== "string") return null
  const m = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!m) return null
  const y = Number(m[1])
  const mo = Number(m[2])
  const d = Number(m[3])
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return null
  const probe = new Date(Date.UTC(y, mo - 1, d))
  if (probe.getUTCFullYear() !== y || probe.getUTCMonth() !== mo - 1 || probe.getUTCDate() !== d) return null
  return [y, mo, d]
}

function parseHm(value: unknown): [number, number, number] | null {
  if (typeof value !== "string") return null
  const m = value.trim().match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/)
  if (!m) return null
  const h = Number(m[1])
  const mi = Number(m[2])
  const s = Number(m[3] ?? "0")
  if (h > 23 || mi > 59 || s > 59) return null
  return [h, mi, s]
}

export function zonedWallClockToUtc(
  dateStr: unknown,
  timeStr: unknown,
  timeZone: string = APP_TIMEZONE
): Date | null {
  const ymd = parseYmd(dateStr)
  const hms = parseHm(timeStr)
  if (!ymd || !hms) return null
  const [y, mo, d] = ymd
  const [h, mi, s] = hms
  const wallAsUtc = Date.UTC(y, mo - 1, d, h, mi, s)
  if (Number.isNaN(wallAsUtc)) return null
  const wallInstant = new Date(wallAsUtc)
  const firstOffset = zoneOffsetMs(wallInstant, timeZone)
  const firstPass = new Date(wallAsUtc - firstOffset)
  const secondOffset = zoneOffsetMs(firstPass, timeZone)
  return secondOffset === firstOffset ? firstPass : new Date(wallAsUtc - secondOffset)
}

export function shiftYmd(dateStr: unknown, days: number): string | null {
  const ymd = parseYmd(dateStr)
  if (!ymd) return null
  const [y, mo, d] = ymd
  const shifted = new Date(Date.UTC(y, mo - 1, d + days))
  return `${shifted.getUTCFullYear()}-${String(shifted.getUTCMonth() + 1).padStart(2, "0")}-${String(shifted.getUTCDate()).padStart(2, "0")}`
}

export interface ZonedDayBounds {
  start: Date
  end: Date
}

export function zonedDayBoundsUtc(dateStr: unknown, timeZone: string = APP_TIMEZONE): ZonedDayBounds | null {
  const start = zonedWallClockToUtc(dateStr, "00:00", timeZone)
  const nextStr = shiftYmd(dateStr, 1)
  if (!start || !nextStr) return null
  const end = zonedWallClockToUtc(nextStr, "00:00", timeZone)
  if (!end) return null
  return { start, end }
}
